import { FC, useEffect, useState } from "react";
import {
  Button,
  Input,
  Popconfirm,
  Popover,
  Space,
  Table,
  TableColumnsType,
  Tag,
} from "antd";
import {
  SearchOutlined,
  TruckOutlined,
  CalendarOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useVesselStore } from "../store/vessel.store";
import { useDeleteVessel, useGetVessels, VESSEL_KEYS } from "~/services/vessel";
import { TVessel } from "~/types";
import { ChevronRight, UserPlus } from "lucide-react";
import { VesselStaff } from "~/types/vessel-staff";
import { Maintenance, VesselStatus } from "~/utils/constants";
import { capitalize } from "~/utils";
import { StaffAssignmentModal } from "../../vessels-shuttles/staff-assignment-modal";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useNotification } from "~/hooks";
import { useQueryClient } from "@tanstack/react-query";

export const VehicleInventory: FC = () => {
  const [openStaffAssignment, setOpenStaffAssignment] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<VesselStatus | "all">("all");

  const [search] = useDebounceValue(searchTerm, 500);

  const { filters, setFilters } = useVesselStore();
  const { data: vessels, isLoading } = useGetVessels(filters);

  useEffect(() => {
    const filters: Record<string, any> = {};
    if (status === "all") {
      filters.vesselStatus = "";
    } else {
      filters.vesselStatus = status;
    }

    filters.search = search;
    setFilters(filters);
  }, [setFilters, status, search]);

  const columns: TableColumnsType<TVessel> = [
    {
      title: "Vehicle",
      dataIndex: "name",
      render: (text: string, vessel) => (
        <Space>
          <TruckOutlined
            className={
              vessel.vesselStatus === VesselStatus.AVAILABLE
                ? "text-blue-600"
                : vessel.vesselStatus === VesselStatus.IN_USE
                  ? "text-yellow-600"
                  : "text-red-600"
            }
          />
          <div className="flex flex-col">
            <span className="text-foreground">{vessel.vesselRef}</span>
            <span className="font-medium">{text}</span>
          </div>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "vesselType",
      key: "type",
      render: (vesselType) => <span>{vesselType?.name || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "vesselStatus",
      render: (status: string) => (
        <Tag
          color={
            status === VesselStatus.AVAILABLE
              ? "green"
              : status === VesselStatus.IN_USE
                ? "yellow"
                : "red"
          }
          className="rounded-full px-2 py-0.5 text-xs"
        >
          {capitalize(status)}
        </Tag>
      ),
    },
    {
      title: "Dimensions (L×H)",
      dataIndex: "dimensions",
      key: "dimensions",
      render: (_, vessel) => (
        <Popover
          content={
            <div>
              <div>Box Length: {vessel.boxLength}"</div>
              <div>
                Door (W×H): {vessel.doorWidth}" × {vessel.doorHeight}"
              </div>
              <div>Internal Height: {vessel.internalHeight}"</div>
              <div>
                Overall (L×H): {vessel.overallLength}' × {vessel.overallHeight}'
              </div>
            </div>
          }
        >
          <Button type="link" className="flex items-center">
            <span>View Details</span>
            <ChevronRight size={16} />
          </Button>
        </Popover>
      ),
    },
    {
      title: "Hourly Rate",
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      render: (hourlyRate) => (
        <span className="font-medium">
          {hourlyRate ? `$${Number(hourlyRate ?? "0").toFixed(2)}` : "Not set"}
        </span>
      ),
    },
    {
      title: "Direct Cost",
      dataIndex: "directCost",
      key: "directCost",
      render: (directCost) => (
        <span className="font-medium">
          {directCost ? `$${Number(directCost ?? "0").toFixed(2)}` : "Not set"}
        </span>
      ),
    },
    {
      title: "Driver(s)",
      dataIndex: "vesselStaffs",
      render: (drivers: VesselStaff[], vessel) => (
        <Space size={-8}>
          {drivers.map((driver, idx) => (
            <div
              key={idx}
              className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs"
            >
              {driver.staff?.user?.firstName?.[0].toUpperCase()}
            </div>
          ))}
          <Button type="link" onClick={() => setOpenStaffAssignment(vessel.id)}>
            <UserPlus size={16} />
          </Button>
        </Space>
      ),
    },
    {
      title: "Maintenance",
      dataIndex: "maintenance",
      render: (status) => (
        <Tag
          color={
            status === Maintenance.COMPLETED
              ? "green"
              : status === Maintenance.IN_SERVICE
                ? "yellow"
                : "white"
          }
          className="rounded-full px-2 py-0.5 text-xs"
        >
          {capitalize(status)}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => <RowAction vessel={record} />,
    },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-medium text-base">Vehicle Inventory</h3>
        <Space size="middle">
          <Space size="small">
            {["all", ...Object.values(VesselStatus)].map((s) => (
              <Button
                key={s}
                size="small"
                className={`hover:bg-gray-100 ${status === s ? "bg-blue-100 text-blue-700" : ""
                  }`}
                onClick={() => {
                  setStatus(s as VesselStatus);
                }}
              >
                {capitalize(s)}
              </Button>
            ))}
          </Space>
          <Input
            placeholder="Search vehicles..."
            prefix={<SearchOutlined className="text-gray-400" />}
            className="w-48 rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Space>
      </div>

      <StaffAssignmentModal
        open={openStaffAssignment}
        onClose={() => setOpenStaffAssignment(undefined)}
      />
      <Table
        rowKey={(row) => row.id}
        loading={isLoading}
        columns={columns}
        dataSource={vessels?.items}
        pagination={{
          total: vessels?.meta.totalItems,
          current: filters.page,
          pageSize: filters.pageSize,
          onChange: (page, pageSize) => {
            setFilters({ page, pageSize });
          },
        }}
        className="text-sm"
        rowClassName="hover:bg-gray-50"
      />
    </div>
  );
};

const RowAction = ({ vessel }: { vessel: TVessel }) => {
  const qc = useQueryClient();

  const { success, error } = useNotification();

  const { filters, openVesselModal } = useVesselStore();
  const mutation = useDeleteVessel();

  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="flex space-x-2">
      <Button
        type="text"
        icon={<CalendarOutlined />}
        className="text-gray-500 hover:text-blue-600"
      />
      <Button
        type="text"
        icon={<EditOutlined />}
        className="text-gray-500 hover:text-blue-600"
        onClick={() => openVesselModal(vessel.id)}
      />
      <Popconfirm
        open={showConfirm}
        title="Are you sure you want to delete this vessel?"
        onConfirm={() => {
          mutation.mutate(vessel.id, {
            onSuccess: () => {
              setShowConfirm(false);
              success({
                message: "Vessel deleted successfully",
              });
              qc.invalidateQueries({
                queryKey: VESSEL_KEYS.GET_VESSELS(filters),
              });
            },
            onError: (err) => {
              // Handle error
              console.log("error", err);
              setShowConfirm(false);
              error({
                message: "Error deleting vessel",
                description: err.message,
              });
            },
          });
        }}
        onCancel={() => setShowConfirm(false)}
        okButtonProps={{ loading: mutation.isPending }}
        okText="Yes"
        cancelText="No"
      >
        <Button
          type="text"
          danger
          onClick={() => setShowConfirm(true)}
          icon={<DeleteOutlined />}
          className="text-gray-500 hover:text-red-600"
        />
      </Popconfirm>
    </div>
  );
};
